// TrendChart组件不需要React导入，因为使用了新的JSX转换
import { useState, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
  ReferenceLine
} from 'recharts';
import { DailyTrendData } from '../types';
import { 
  getChinaDate, 
  getChinaDateStart, 
  getChinaDateEnd,
  getChinaMonthStart,
  formatDateForInput as formatDateForInputUtil, 
  formatToChinaTime, 
  isToday 
} from '../utils/time';

/**
 * 日期范围预设选项
 */
enum DateRangePreset {
  LAST_7_DAYS = 'last7',
  LAST_15_DAYS = 'last15', 
  LAST_30_DAYS = 'last30',
  CURRENT_MONTH = 'currentMonth',
  CUSTOM = 'custom'
}

/**
 * 日期范围预设配置
 */
const dateRangeOptions = [
  { value: DateRangePreset.LAST_7_DAYS, label: '最近7天' },
  { value: DateRangePreset.LAST_15_DAYS, label: '最近15天' },
  { value: DateRangePreset.LAST_30_DAYS, label: '最近30天' },
  { value: DateRangePreset.CURRENT_MONTH, label: '当月' },
  { value: DateRangePreset.CUSTOM, label: '自定义范围' }
];

interface TrendChartProps {
  data: DailyTrendData[];
  title?: string;
  height?: number;
  showRating?: boolean;
}

/**
 * 趋势图组件
 * 显示按天统计的销量/评论走势图和评分走势图
 */
const TrendChart: React.FC<TrendChartProps> = ({
  data,
  height = 300,
  showRating = true
}) => {
  // 日期筛选状态
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>(DateRangePreset.LAST_7_DAYS);
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');

  /**
   * 根据预设获取日期范围（使用中国时区）
   */
  const getDateRange = (preset: DateRangePreset): { startDate: Date; endDate: Date } => {
    // 使用中国时区的今天结束时间
    const endDate = getChinaDateEnd(0);
    
    let startDate: Date;
    
    switch (preset) {
      case DateRangePreset.LAST_7_DAYS:
        startDate = getChinaDateStart(-6); // 最近7天：今天往前6天
        break;
      case DateRangePreset.LAST_15_DAYS:
        startDate = getChinaDateStart(-14); // 最近15天：今天往前14天
        break;
      case DateRangePreset.LAST_30_DAYS:
        startDate = getChinaDateStart(-29); // 最近30天：今天往前29天
        break;
      case DateRangePreset.CURRENT_MONTH:
        startDate = getChinaMonthStart(0); // 当月第一天
        break;
      case DateRangePreset.CUSTOM:
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          return {
            startDate,
            endDate: new Date(customEndDate)
          };
        }
        // 如果自定义日期没有设置，默认使用最近7天
        startDate = getChinaDateStart(-6);
        break;
      default:
        startDate = getChinaDateStart(-6);
    }
    
    return { startDate, endDate };
  };

  /**
   * 格式化日期为中国时区YYYY-MM-DD格式
   */
  const formatDateToLocal = (date: Date): string => {
    return date.toLocaleDateString('sv-SE', {
      timeZone: 'Asia/Shanghai'
    }); // 使用中国时区的YYYY-MM-DD格式
  };

  /**
   * 筛选数据
   */
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    const { startDate, endDate } = getDateRange(selectedPreset);
    
    return data.filter(item => {
      const itemDate = new Date(item.date);
      return itemDate >= startDate && itemDate <= endDate;
    }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [data, selectedPreset, customStartDate, customEndDate]);

  /**
   * 生成完整的时间范围数据（包括空数据点）
   */
  const completeTimeRangeData = useMemo(() => {
    const { startDate, endDate } = getDateRange(selectedPreset);
    const completeData = [];
    
    // 生成完整的日期范围
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dateStr = formatDateToLocal(currentDate);
      
      // 查找该日期是否有实际数据
      const existingData = filteredData.find(item => item.date === dateStr);
      
      if (existingData) {
        completeData.push(existingData);
      } else {
        // 补充空数据点
        completeData.push({
          date: dateStr,
          sales_increment: 0,
          review_increment: 0,
          sales_count: 0,
          review_count: 0,
          product_rating: 0
        });
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return completeData;
  }, [filteredData, selectedPreset, customStartDate, customEndDate]);

  /**
   * 计算数据覆盖率和统计信息
   */
  const dataStats = useMemo(() => {
    const totalDays = completeTimeRangeData.length;
    const daysWithData = filteredData.length;
    const daysWithSalesData = filteredData.filter(item => item.sales_increment > 0 || item.sales_count > 0).length;
    const daysWithReviewData = filteredData.filter(item => item.review_increment > 0 || item.review_count > 0).length;
    
    return {
      totalDays,
      daysWithData,
      daysWithSalesData,
      daysWithReviewData,
      coverage: daysWithData > 0 ? Math.round((daysWithData / totalDays) * 100) : 0
    };
  }, [completeTimeRangeData, filteredData]);

  /**
   * 处理预设选择变化
   */
  const handlePresetChange = (preset: DateRangePreset) => {
    setSelectedPreset(preset);
    
    // 如果不是自定义范围，清空自定义日期
    if (preset !== DateRangePreset.CUSTOM) {
      setCustomStartDate('');
      setCustomEndDate('');
    }
  };

  /**
   * 格式化日期为YYYY-MM-DD格式（用于输入框）
   */
  const formatDateForInput = (date: Date): string => {
    return formatDateToLocal(date);
  };

  /**
   * 初始化自定义日期范围（使用中国时区）
   */
  const initializeCustomDateRange = () => {
    if (!customStartDate || !customEndDate) {
      // 使用中国时区时间设置默认范围（最近7天）
      const endDate = getChinaDateStart(0); // 中国时区今天
      const startDate = getChinaDateStart(-6); // 中国时区6天前
      
      setCustomEndDate(formatDateForInputUtil(endDate));
      setCustomStartDate(formatDateForInputUtil(startDate));
    }
  };
  // 如果没有原始数据，显示空状态
  if (!data || data.length === 0) {
    return (
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body text-center py-12">
          <div className="text-6xl mb-4">📊</div>
          <h3 className="text-xl font-bold text-base-content/80 mb-2">
            暂无趋势数据
          </h3>
          <p className="text-base-content/60">
            等待系统收集更多监控数据后显示趋势图
          </p>
        </div>
      </div>
    );
  }

  // 格式化日期显示
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
      });
    } catch (error) {
      return dateString;
    }
  };

  // 转换数据格式 - 使用完整的时间范围数据
  const chartData = completeTimeRangeData.map(item => ({
    ...item,
    formattedDate: formatDate(item.date),
    displaySales: item.sales_increment, // 改为显示销量增量
    displayReviews: item.review_increment, // 改为显示评论增量
    displayRating: item.product_rating || 0,
    // 保留原始总数数据用于Tooltip显示
    totalSales: item.sales_count,
    totalReviews: item.review_count
  }));

  /**
   * 计算平均销量增量
   */
  const averageSalesIncrement = useMemo(() => {
    if (chartData.length === 0) return 0;
    
    // 只计算有增量数据的天数
    const daysWithSales = chartData.filter(item => item.displaySales > 0);
    if (daysWithSales.length === 0) return 0;
    
    const totalIncrement = daysWithSales.reduce((sum, item) => sum + item.displaySales, 0);
    return Math.round(totalIncrement / daysWithSales.length);
  }, [chartData]);

  /**
   * 计算平均评论增量
   */
  const averageReviewsIncrement = useMemo(() => {
    if (chartData.length === 0) return 0;
    
    // 只计算有增量数据的天数
    const daysWithReviews = chartData.filter(item => item.displayReviews > 0);
    if (daysWithReviews.length === 0) return 0;
    
    const totalIncrement = daysWithReviews.reduce((sum, item) => sum + item.displayReviews, 0);
    return Math.round(totalIncrement / daysWithReviews.length);
  }, [chartData]);

  // 自定义Tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      
      // 简化逻辑：基准日的判断条件更严格
      // 基准日必须同时满足：1) 增量为0  2) 是历史上第一个有数据的日期  3) 不是今天
      const today = getChinaDate();
      const isTodayData = data.date === today;
      
      // 找到数据序列中第一个有数据的日期（按时间顺序）
      const sortedData = [...chartData].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      const firstDataDate = sortedData.find(item => item.totalSales > 0 || item.totalReviews > 0)?.date;
      
      // 更严格的基准日判断
      const isBaselineDay = data.date === firstDataDate && 
                           data.sales_increment === 0 && 
                           data.review_increment === 0 && 
                             !isTodayData; // 今天永远不是基准日
      
      return (
        <div className="bg-base-100 p-4 rounded-lg shadow-lg border border-base-300">
          <p className="font-semibold text-base-content">{`日期: ${data.date}`}</p>
          {isBaselineDay && (
            <p className="text-xs text-base-content/60 mb-2">📊 基准数据日</p>
          )}
          <div className="space-y-1 mt-2">
            <p style={{ color: '#3B82F6' }}>
              {`销量增量: ${data.displaySales.toLocaleString('zh-CN')}`}
              <span className="text-base-content/60 ml-2 text-xs">
                (总计: {data.totalSales.toLocaleString('zh-CN')})
              </span>
            </p>
            <p style={{ color: '#10B981' }}>
              {`评论增量: ${data.displayReviews.toLocaleString('zh-CN')}`}
              <span className="text-base-content/60 ml-2 text-xs">
                (总计: {data.totalReviews.toLocaleString('zh-CN')})
              </span>
            </p>
            {showRating && data.product_rating && (
              <p style={{ color: '#F59E0B' }}>
                {`评分: ${data.product_rating.toFixed(1)}`}
              </p>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  // 评分Tooltip
  const RatingTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-base-100 p-4 rounded-lg shadow-lg border border-base-300">
          <p className="font-semibold text-base-content">{`日期: ${data.date}`}</p>
          <p style={{ color: '#F59E0B' }} className="mt-2">
            {`评分: ${data.displayRating.toFixed(1)}`}
          </p>
        </div>
      );
    }
    return null;
  };

  // 自定义销量增量标签
  const SalesLabel = (props: any) => {
    const { x, y, value, ...restProps } = props;
    if (value === 0) return null; // 不显示0值标签
    
    // 过滤掉React不识别的DOM属性
    const { 
      dataKey, 
      index, 
      payload, 
      points, 
      parentViewBox, 
      textBreakAll, 
      key,
      ...cleanProps 
    } = restProps;
    
    return (
      <text 
        x={x - 15} 
        y={y - 10} 
        textAnchor="middle" 
        fontSize="14" 
        fill="#3B82F6" 
        fontWeight="700"
        {...cleanProps}
      >
        +{value.toLocaleString('zh-CN')}
      </text>
    );
  };

  // 自定义评论增量标签
  const ReviewsLabel = (props: any) => {
    const { x, y, value, ...restProps } = props;
    if (value === 0) return null; // 不显示0值标签
    
    // 过滤掉React不识别的DOM属性
    const { 
      dataKey, 
      index, 
      payload, 
      points, 
      parentViewBox, 
      textBreakAll, 
      key,
      ...cleanProps 
    } = restProps;
    
    return (
      <text 
        x={x + 15} 
        y={y - 25} 
        textAnchor="middle" 
        fontSize="14" 
        fill="#10B981" 
        fontWeight="700"
        {...cleanProps}
      >
        +{value.toLocaleString('zh-CN')}
      </text>
    );
  };

  // 自定义评分标签
  const RatingLabel = (props: any) => {
    const { x, y, value, ...restProps } = props;
    if (value === 0) return null; // 不显示0值标签
    
    // 过滤掉React不识别的DOM属性
    const { 
      dataKey, 
      index, 
      payload, 
      points, 
      parentViewBox, 
      textBreakAll, 
      key,
      ...cleanProps 
    } = restProps;
    
    return (
      <text 
        x={x} 
        y={y - 10} 
        textAnchor="middle" 
        fontSize="12" 
        fill="#F59E0B" 
        fontWeight="600"
        {...cleanProps}
      >
        {value.toFixed(1)}
      </text>
    );
  };

  return (
    <div className="space-y-6">
      {/* 销量和评论走势图 */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <div className="flex flex-col gap-4 mb-4">
            {/* 标题行 */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <h3 className="card-title">
              <span className="text-accent">📈</span>
                  销量与评论增量趋势
            </h3>
                {/* 数据统计标签 */}
                <div className="flex flex-wrap items-center gap-2">
                  {averageSalesIncrement > 0 && (
                    <div className="badge bg-blue-500 text-white py-2">
                      日均销量: {averageSalesIncrement.toLocaleString('zh-CN')}
                    </div>
                  )}
                  {averageReviewsIncrement > 0 && (
                    <div className="badge bg-green-500 text-white py-2">
                      日均评论: {averageReviewsIncrement.toLocaleString('zh-CN')}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col xl:flex-row items-start xl:items-center gap-3">
                {/* 日期筛选器 */}
                <div className="flex flex-col lg:flex-row gap-2">
                  {/* 预设选项 */}
                  <div className="flex flex-wrap gap-1">
                    {dateRangeOptions.map(option => (
                      <button
                        key={option.value}
                        className={`btn btn-xs ${
                          selectedPreset === option.value
                            ? 'btn-primary'
                            : 'btn-outline btn-primary'
                        }`}
                        onClick={() => {
                          handlePresetChange(option.value);
                          if (option.value === DateRangePreset.CUSTOM) {
                            initializeCustomDateRange();
                          }
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* 自定义日期选择器 - 单独一行 */}
            {selectedPreset === DateRangePreset.CUSTOM && (
              <div className="flex items-center gap-2 flex-wrap justify-end bg-base-200/30 p-3 rounded-lg">
                <span className="text-sm text-base-content/70">从</span>
                <input
                  type="date"
                  className="input input-sm input-bordered"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  max={customEndDate || formatDateForInputUtil(getChinaDateStart(0))}
                />
                <span className="text-sm text-base-content/70">到</span>
                <input
                  type="date"
                  className="input input-sm input-bordered"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  min={customStartDate}
                  max={formatDateForInputUtil(getChinaDateStart(0))}
                />
              </div>
            )}
          </div>

          {/* 数据状态信息 */}
          {dataStats.totalDays > 0 && (
            <div className="mb-4 p-3 bg-base-200/30 rounded-lg">
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-base-content/70">时间范围:</span>
                  <span className="font-medium">{dataStats.totalDays}天</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-base-content/70">数据覆盖:</span>
                  <span className="font-medium text-blue-600">{dataStats.daysWithData}天</span>
                  <span className="text-base-content/50">({dataStats.coverage}%)</span>
                </div>
                {dataStats.daysWithSalesData > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-base-content/70">销量数据:</span>
                    <span className="font-medium text-blue-600">{dataStats.daysWithSalesData}天</span>
                  </div>
                )}
                {dataStats.daysWithReviewData > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-base-content/70">评论数据:</span>
                    <span className="font-medium text-green-600">{dataStats.daysWithReviewData}天</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 图表区域 - 始终显示 */}
          <div>
          {/* 销量评论走势图 */}
          <ResponsiveContainer width="100%" height={height}>
              <LineChart data={chartData} margin={{ top: 30, right: 50, left: 10, bottom: 35 }}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="formattedDate" 
                className="text-base-content/70"
                fontSize={12}
              />
              <YAxis 
                className="text-base-content/70"
                fontSize={12}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
                
                {/* 为每个数据点添加垂直虚线 */}
                {chartData.map((item) => (
                  item.displaySales > 0 || item.displayReviews > 0 ? (
                    <ReferenceLine
                      key={`ref-line-${item.formattedDate}`}
                      x={item.formattedDate}
                      stroke="#94A3B8"
                      strokeDasharray="2 4"
                      strokeWidth={1}
                      strokeOpacity={0.6}
                    />
                  ) : null
                ))}

                {/* 平均销量增量水平虚线 */}
                {averageSalesIncrement > 0 && (
                  <ReferenceLine
                    y={averageSalesIncrement}
                    stroke="#9CA3AF"
                    strokeDasharray="8 4"
                    strokeWidth={2}
                    strokeOpacity={0.8}
                    label={{
                      value: `日均销量: ${averageSalesIncrement.toLocaleString('zh-CN')}`,
                      position: "insideTopLeft",
                      style: { 
                        fontSize: "12px", 
                        fill: "#6B7280", 
                        fontWeight: "600",
                        textShadow: "1px 1px 2px rgba(255,255,255,0.9)"
                      }
                    }}
                  />
                )}
                
              <Line
                key="sales-line"
                type="monotone"
                dataKey="displaySales"
                  name="销量增量"
                stroke="#3B82F6"
                strokeWidth={2}
                dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#3B82F6', strokeWidth: 2 }}
                  connectNulls={false}
                >
                  <LabelList key="sales-labels" dataKey="displaySales" content={<SalesLabel />} />
                </Line>
              <Line
                key="reviews-line"
                type="monotone"
                dataKey="displayReviews"
                  name="评论增量"
                stroke="#10B981"
                strokeWidth={2}
                dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#10B981', strokeWidth: 2 }}
                  connectNulls={false}
                >
                  <LabelList key="reviews-labels" dataKey="displayReviews" content={<ReviewsLabel />} />
                </Line>
            </LineChart>
          </ResponsiveContainer>
            
            {/* 数据不足提示 */}
            {dataStats.daysWithData === 0 && (
              <div className="text-center py-4 bg-orange-50 rounded-lg mt-4">
                <div className="text-2xl mb-2">📊</div>
                <p className="text-orange-600 font-medium">所选时间范围内暂无监控数据</p>
                <p className="text-orange-500 text-sm mt-1">图表显示完整时间轴，等待系统收集数据</p>
              </div>
            )}
            
            {dataStats.daysWithData > 0 && dataStats.coverage < 50 && (
              <div className="text-center py-3 bg-blue-50 rounded-lg mt-4">
                <p className="text-blue-600 text-sm">
                  💡 当前数据覆盖率较低 ({dataStats.coverage}%)，随着监控时间增长数据会更完整
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 评分走势图 */}
      {showRating && chartData.some(item => item.displayRating > 0) && (
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body py-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="card-title text-lg">
                <span className="text-warning">⭐</span>
                评分趋势
              </h3>
              <div className="flex items-center gap-2">
              <div className="badge badge-warning badge-sm">
                当前评分：{chartData[chartData.length - 1]?.displayRating.toFixed(1) || '0.0'}分
                </div>
                <div className="text-xs text-base-content/60">
                  范围：1.0 - 5.0
                </div>
              </div>
            </div>

            {/* 评分走势图 */}
            <ResponsiveContainer width="100%" height={140}>
                              <LineChart data={chartData} margin={{ top: 25, right: 50, left: 10, bottom: 15 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="formattedDate" 
                  className="text-base-content/70"
                  fontSize={11}
                />
                <YAxis 
                  domain={[1, 5]}
                  ticks={[1, 2, 3, 4, 5]}
                  className="text-base-content/70"
                  fontSize={11}
                />
                <Tooltip content={<RatingTooltip />} />
                <Line
                  key="rating-line"
                  type="monotone"
                  dataKey="displayRating"
                  name="评分"
                  stroke="#F59E0B"
                  strokeWidth={2}
                  dot={{ fill: '#F59E0B', strokeWidth: 1, r: 3 }}
                  activeDot={{ r: 5, stroke: '#F59E0B', strokeWidth: 2, fill: '#FBBF24' }}
                >
                  <LabelList key="rating-labels" dataKey="displayRating" content={<RatingLabel />} />
                </Line>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrendChart; 